import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Droplet, Footprints, TrendingUp, Loader2 } from "lucide-react";

import MacroRing from "../components/MacroRing";
import ActivityChart from "../components/ActivityChart";
import QuickActions from "../components/QuickActions";

import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  // States
  const [todayCalories, setTodayCalories] = useState(0);
  const [activity, setActivity] = useState({ water: 0, steps: 0 });
  const [profile, setProfile] = useState({ weight: 0, target_weight: 0 });

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (!user) return;

    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // Run all fetches in parallel for better performance
        await Promise.all([
          fetchTodayCalories(),
          fetchDailyActivity(),
          fetchProfile(),
        ]);
      } catch (err) {
        console.error("Dashboard Load Error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user]);

  const fetchTodayCalories = async () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from("meal_logs")
      .select("calories")
      .eq("user_id", user.id)
      .gte("created_at", start.toISOString());

    if (!error && data) {
      const total = data.reduce((sum, m) => sum + (m.calories || 0), 0);
      setTodayCalories(total);
    }
  };

  const fetchDailyActivity = async () => {
    const { data, error } = await supabase
      .from("daily_activity")
      .select("water, steps")
      .eq("user_id", user.id)
      .eq("date", today)
      .maybeSingle(); // uses maybeSingle to avoid 406 errors if row doesn't exist yet

    if (!error && data) {
      setActivity({
        water: data.water || 0,
        steps: data.steps || 0,
      });
    }
  };

  const fetchProfile = async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("weight, target_weight")
      .eq("id", user.id)
      .single();

    if (!error && data) {
      setProfile(data);
    }
  };

  const stats = [
    {
      label: "Calories",
      value: Math.round(todayCalories),
      target: 2500,
      unit: "kcal",
      icon: Flame,
      color: "text-orange-500",
      bgColor: "bg-orange-50",
      barColor: "bg-orange-500", // Explicit color for Tailwind JIT
    },
    {
      label: "Water",
      value: (activity.water * 0.25).toFixed(1), // glasses to Liters
      target: 2.5,
      unit: "L",
      icon: Droplet,
      color: "text-blue-500",
      bgColor: "bg-blue-50",
      barColor: "bg-blue-500",
    },
    {
      label: "Steps",
      value: activity.steps,
      target: 10000,
      unit: "",
      icon: Footprints,
      color: "text-emerald-500",
      bgColor: "bg-emerald-50",
      barColor: "bg-emerald-500",
    },
    {
      label: "Weight",
      value: profile.weight,
      target: profile.target_weight,
      unit: "kg",
      icon: TrendingUp,
      color: "text-purple-500",
      bgColor: "bg-purple-50",
      barColor: "bg-purple-500",
    },
  ];

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6 p-4"
    >
      {/* HEADER */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Welcome back!</h2>
        <p className="text-gray-500 mt-1">Let's crush your goals today 💪</p>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const percentage = Math.min(
            (Number(stat.value) / Number(stat.target || 1)) * 100,
            100
          );

          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className={`w-10 h-10 ${stat.bgColor} rounded-xl flex items-center justify-center mb-3`}>
                <Icon className={`w-5 h-5 ${stat.color}`} />
              </div>

              <p className="text-sm text-gray-500 mb-1">{stat.label}</p>

              <div className="flex items-baseline space-x-1">
                <span className="text-xl font-bold text-gray-900">
                  {stat.value}
                </span>
                <span className="text-xs text-gray-400 font-medium uppercase">
                  {stat.unit}
                </span>
              </div>
              
              <div className="text-[10px] text-gray-400 mt-0.5">
                Target: {stat.target}{stat.unit}
              </div>

              {/* PROGRESS BAR */}
              <div className="mt-3 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
                  className={`h-full ${stat.barColor} rounded-full`}
                />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* QUICK ACTIONS */}
      <QuickActions />

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <MacroRing />
        </div>
        <div>
          <ActivityChart />
        </div>
      </div>
    </motion.div>
  );
};

export default Dashboard;